import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Crown, LogOut, Trash2, UserPlus } from 'lucide-react';
import { Dialog, ConfirmDialog } from '../components/Dialog';
import { cn } from '../lib/cn';
import { supabase } from '../sync/useSync';
import { refreshWorkspacesSoon, useOwnUserId, useWorkspaces, useWorkspaceUi } from './state';

const field = 'w-full rounded-lg border border-border bg-bg px-2 py-1.5 text-sm';
const secondary =
  'flex items-center gap-1.5 rounded-lg border border-border px-2 py-1 text-sm hover:bg-bg-hover disabled:opacity-50';
const select = 'rounded-lg border border-border bg-bg px-1.5 py-1 text-xs';

interface MemberRow {
  userId: string;
  permission: 'read' | 'write';
  status: 'pending' | 'active';
  email: string | null;
  displayName: string | null;
}

export function ManageWorkspaceDialog() {
  const { t } = useTranslation();
  const workspaceId = useWorkspaceUi((s) => s.manageId);
  const close = useWorkspaceUi((s) => s.close);
  const workspace = useWorkspaces().find((w) => w.id === workspaceId);

  return (
    <Dialog
      open={!!workspaceId}
      onOpenChange={(o) => !o && close()}
      title={t('workspaces.manageTitle', { name: workspace?.name ?? '' })}
    >
      {workspaceId && workspace && <ManageBody workspaceId={workspaceId} workspace={workspace} />}
    </Dialog>
  );
}

function ManageBody({
  workspaceId,
  workspace,
}: {
  workspaceId: string;
  workspace: { name: string; permission: 'read' | 'write'; createdBy: string };
}) {
  const { t } = useTranslation();
  const close = useWorkspaceUi((s) => s.close);
  const userId = useOwnUserId();
  const isCreator = userId === workspace.createdBy;
  const canManage = workspace.permission === 'write';
  const [members, setMembers] = useState<MemberRow[] | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [confirmRemove, setConfirmRemove] = useState<MemberRow | null>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);

  const load = () =>
    void supabase()
      .functions.invoke<{ members?: MemberRow[] }>('workspace-members', {
        body: { action: 'list-members', workspaceId },
      })
      .then(({ data }) => setMembers(data?.members ?? []));

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workspaceId]);

  const call = async (body: Record<string, unknown>) => {
    const { error: err } = await supabase().functions.invoke('workspace-members', { body });
    if (err) throw err;
  };

  const rename = async (name: string) => {
    if (!name || name === workspace.name) return;
    setBusy('rename');
    setError(null);
    try {
      await call({ action: 'rename', workspaceId, name });
      refreshWorkspacesSoon();
    } catch {
      setError(t('workspaces.actionFailed'));
    } finally {
      setBusy(null);
    }
  };

  const setPermission = async (member: MemberRow, permission: 'read' | 'write') => {
    setBusy(member.userId);
    setError(null);
    try {
      await call({ action: 'update-permission', workspaceId, userId: member.userId, permission });
      load();
      refreshWorkspacesSoon();
    } catch {
      setError(t('workspaces.actionFailed'));
    } finally {
      setBusy(null);
    }
  };

  const remove = async (member: MemberRow) => {
    setBusy(member.userId);
    setError(null);
    try {
      await call({ action: 'remove', workspaceId, userId: member.userId });
      load();
      refreshWorkspacesSoon();
      if (member.userId === userId) close();
    } catch {
      setError(t('workspaces.actionFailed'));
    } finally {
      setBusy(null);
      setConfirmRemove(null);
    }
  };

  const deleteWorkspace = async () => {
    setBusy('delete');
    setError(null);
    try {
      await call({ action: 'delete-workspace', workspaceId });
      close();
      refreshWorkspacesSoon();
    } catch {
      setError(t('workspaces.actionFailed'));
    } finally {
      setBusy(null);
      setConfirmDelete(false);
    }
  };

  return (
    <div className="flex flex-col gap-4 text-sm">
      {canManage ? (
        <label className="flex flex-col gap-1">
          {t('workspaces.nameLabel')}
          <input
            key={workspace.name}
            defaultValue={workspace.name}
            disabled={busy === 'rename'}
            aria-label={t('workspaces.nameLabel')}
            onBlur={(e) => void rename(e.target.value.trim())}
            onKeyDown={(e) => {
              if (e.key === 'Enter') e.currentTarget.blur();
            }}
            className={field}
          />
        </label>
      ) : (
        <div className="flex flex-col gap-1">
          <span className="text-xs text-fg-muted">{t('workspaces.nameLabel')}</span>
          <span>{workspace.name}</span>
        </div>
      )}

      <div className="flex flex-col gap-2">
        <span className="font-medium">{t('workspaces.members')}</span>
        {members === null && <p className="text-xs text-fg-muted">{t('workspaces.loading')}</p>}
        {members?.map((m) => (
          <div
            key={m.userId}
            className="flex flex-wrap items-center justify-between gap-x-2 gap-y-1"
          >
            {/* On a narrow screen the permission list wraps below the name. */}
            <span className="flex min-w-[12rem] flex-1 items-center gap-1.5">
              {m.userId === workspace.createdBy && (
                <Crown className="size-3.5 shrink-0 text-accent" />
              )}
              {/* Name, then address and invite state below it: next to the permission
                  list there is no room for all of it on one line. */}
              <span className="flex min-w-0 flex-col">
                <span className="truncate">
                  {m.displayName ?? m.email ?? m.userId}
                  {m.userId === userId && ` (${t('workspaces.you')})`}
                </span>
                {m.displayName && m.email && (
                  <span className="truncate text-xs text-fg-muted" title={m.email}>
                    {m.email}
                  </span>
                )}
                {m.status === 'pending' && (
                  <span className="text-xs text-fg-muted">{t('workspaces.pending')}</span>
                )}
              </span>
            </span>
            {isCreator && m.userId !== workspace.createdBy ? (
              <div className="flex shrink-0 items-center gap-1.5">
                <select
                  value={m.permission}
                  disabled={busy === m.userId}
                  onChange={(e) => void setPermission(m, e.target.value as 'read' | 'write')}
                  className={select}
                >
                  <option value="read">{t('workspaces.permissionRead')}</option>
                  <option value="write">{t('workspaces.permissionWrite')}</option>
                </select>
                <button
                  type="button"
                  title={t('workspaces.removeMember')}
                  aria-label={t('workspaces.removeMember')}
                  disabled={busy === m.userId}
                  onClick={() => setConfirmRemove(m)}
                  className="grid size-7 place-items-center rounded-md text-fg-muted hover:bg-bg-hover hover:text-danger"
                >
                  <Trash2 className="size-3.5" />
                </button>
              </div>
            ) : (
              <span className="shrink-0 text-xs text-fg-muted">
                {m.userId === workspace.createdBy
                  ? t('workspaces.owner')
                  : t(`workspaces.permission${m.permission === 'write' ? 'Write' : 'Read'}`)}
              </span>
            )}
          </div>
        ))}
      </div>

      {error && (
        <p role="alert" className="text-xs text-danger">
          {error}
        </p>
      )}

      {canManage && <AddMemberForm workspaceId={workspaceId} onAdded={load} />}

      <div className="flex flex-wrap items-center gap-2 border-t border-border pt-3">
        {!isCreator && userId && (
          <button
            type="button"
            disabled={busy !== null}
            onClick={() =>
              void remove({
                userId,
                permission: workspace.permission,
                status: 'active',
                email: null,
                displayName: null,
              })
            }
            className={secondary}
          >
            <LogOut className="size-3.5" />
            {t('workspaces.leave')}
          </button>
        )}
        {isCreator && (
          <button
            type="button"
            disabled={busy !== null}
            onClick={() => setConfirmDelete(true)}
            className={cn(secondary, 'text-danger')}
          >
            <Trash2 className="size-3.5" />
            {t('workspaces.delete')}
          </button>
        )}
      </div>

      <ConfirmDialog
        open={!!confirmRemove}
        onOpenChange={(o) => !o && setConfirmRemove(null)}
        title={t('workspaces.removeConfirmTitle')}
        description={t('workspaces.removeConfirmBody', {
          email: confirmRemove?.displayName ?? confirmRemove?.email ?? confirmRemove?.userId ?? '',
        })}
        confirmLabel={t('workspaces.removeMember')}
        cancelLabel={t('app.cancel')}
        onConfirm={() => confirmRemove && void remove(confirmRemove)}
      />
      <ConfirmDialog
        open={confirmDelete}
        onOpenChange={setConfirmDelete}
        title={t('workspaces.deleteConfirmTitle')}
        description={t('workspaces.deleteConfirmBody')}
        confirmLabel={t('workspaces.delete')}
        cancelLabel={t('app.cancel')}
        onConfirm={() => void deleteWorkspace()}
      />
    </div>
  );
}

function AddMemberForm({ workspaceId, onAdded }: { workspaceId: string; onAdded: () => void }) {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [permission, setPermissionValue] = useState<'read' | 'write'>('read');
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState<{ error: boolean; text: string } | null>(null);

  return (
    <form
      aria-label={t('workspaces.addMember')}
      className="flex flex-col gap-2"
      onSubmit={(e) => {
        e.preventDefault();
        setBusy(true);
        setNote(null);
        void supabase()
          .functions.invoke<{ ok?: boolean; error?: string }>('workspace-members', {
            body: { action: 'add', workspaceId, email: email.trim(), permission },
          })
          .then(({ error }) => {
            if (!error) {
              setNote({ error: false, text: t('workspaces.added', { email: email.trim() }) });
              setEmail('');
              onAdded();
              refreshWorkspacesSoon();
              return;
            }
            setNote({ error: true, text: t('workspaces.addFailed') });
          })
          .finally(() => setBusy(false));
      }}
    >
      <span className="font-medium">{t('workspaces.addMember')}</span>
      <div className="flex flex-wrap gap-2">
        <input
          type="email"
          required
          aria-label={t('sync.email')}
          placeholder={t('sync.email')}
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          className={cn(field, 'w-auto min-w-[12rem] flex-1')}
        />
        <select
          value={permission}
          onChange={(e) => setPermissionValue(e.target.value as 'read' | 'write')}
          className={select}
        >
          <option value="read">{t('workspaces.permissionRead')}</option>
          <option value="write">{t('workspaces.permissionWrite')}</option>
        </select>
        <button type="submit" disabled={busy} className={cn(secondary, 'shrink-0')}>
          <UserPlus className="size-3.5" />
          {t('workspaces.addMemberButton')}
        </button>
      </div>
      {note && (
        <p className={cn('text-xs', note.error ? 'text-danger' : 'text-fg-muted')}>{note.text}</p>
      )}
    </form>
  );
}
